import type { ErrorDetailDTO, ErrorSummaryItemDTO, NomiFileDTO } from '../types'
import type { ColumnsType } from 'antd/es/table'

import {
  ReloadOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons'
import {
  Table,
  Card,
  Tag,
  Space,
  Typography,
  Tooltip,
  Badge,
  Button,
  Row,
  Col,
  Statistic,
  Alert
} from 'antd'

import { useNomi835Status } from '../hooks'
import { fmtDateTime } from '../utils'

import { statusTag } from './status-tag'

const { Title } = Typography

const LastFilesTable: React.FC<{ data: NomiFileDTO[]; loading: boolean; onReload: () => void }> = ({
  data,
  loading,
  onReload
}) => {
  const columns: ColumnsType<NomiFileDTO> = [
    {
      title: 'File name',
      dataIndex: 'file_name',
      key: 'file_name',
      render: (v, r) => (
        <Space direction='vertical' size={0}>
          <Space>
            <FileTextOutlined />
            <Typography.Text strong>{v}</Typography.Text>
          </Space>
          <Typography.Text type='secondary' style={{ fontSize: 12 }}>
            created {fmtDateTime(r.created_at)}
          </Typography.Text>
        </Space>
      )
    },
    {
      title: 'EOB / date / #',
      key: 'meta',
      render: (_, r) => (
        <Space direction='vertical' size={2}>
          <Typography.Text>
            EOB: <Typography.Text code>{r.eob}</Typography.Text>
          </Typography.Text>
          <Typography.Text type='secondary'>
            {r.sending_date} • #{r.counter}
          </Typography.Text>
        </Space>
      )
    },
    {
      title: 'From (SMB)',
      key: 'from',
      render: (_, r) => (
        <div>
          <div>{r.smb_share ?? '—'}</div>
          <Typography.Text type='secondary' style={{ fontSize: 12 }}>
            {r.smb_path ?? ''}
          </Typography.Text>
        </div>
      )
    },
    {
      title: 'To (SFTP)',
      key: 'to',
      render: (_, r) => (
        <div>
          <div>{r.sftp_destination_host}</div>
          <Typography.Text type='secondary' style={{ fontSize: 12 }}>
            {r.sftp_destination_path}
          </Typography.Text>
        </div>
      )
    },
    {
      title: 'S3 backup',
      dataIndex: 's3_backup_path',
      key: 's3_backup_path',
      ellipsis: true,
      render: (v: string) => (
        <Tooltip title={v}>
          <Typography.Text>{v}</Typography.Text>
        </Tooltip>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (v: string) => statusTag(v)
    }
  ]

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Last 5 NOMI 835 files
          </Typography.Title>
          <Tooltip title='Reload'>
            <Button icon={<ReloadOutlined />} onClick={onReload} size='small' />
          </Tooltip>
        </Space>
      }
      className='rounded-2xl'
    >
      <Table<NomiFileDTO>
        rowKey={r => r.id}
        dataSource={data}
        columns={columns}
        loading={loading}
        pagination={false}
        size='middle'
      />
    </Card>
  )
}

const ErrorSummaryTable: React.FC<{
  data: ErrorSummaryItemDTO[]
  loading: boolean
  onReload: () => void
}> = ({ data, loading, onReload }) => {
  const columns: ColumnsType<ErrorSummaryItemDTO> = [
    {
      title: 'File',
      dataIndex: 'file_name',
      key: 'file_name',
      render: v => (
        <Space>
          <ExclamationCircleOutlined />
          <Typography.Text strong>{v || '(no file)'}</Typography.Text>
        </Space>
      )
    },
    {
      title: 'Totals',
      key: 'totals',
      render: (_, r) => (
        <Space size={12}>
          <Badge count={r.total} showZero>
            <span style={{ paddingRight: 6 }}>all</span>
          </Badge>
          <Badge count={r.open} color='#faad14' showZero>
            <span style={{ paddingRight: 6 }}>open</span>
          </Badge>
        </Space>
      )
    },
    {
      title: 'Stages',
      key: 'stages',
      render: (_, r) => (
        <Space wrap>
          {Object.entries(r.stages || {}).map(([stage, cnt]) => (
            <Tag key={stage}>
              {stage}: {cnt}
            </Tag>
          ))}
        </Space>
      )
    },
    {
      title: 'First / Last seen',
      key: 'seen',
      render: (_, r) => (
        <Space direction='vertical' size={0}>
          <Typography.Text type='secondary'>first: {fmtDateTime(r.first_seen_at)}</Typography.Text>
          <Typography.Text type='secondary'>last: {fmtDateTime(r.last_seen_at)}</Typography.Text>
        </Space>
      )
    }
  ]

  const expandedRowRender = (record: ErrorSummaryItemDTO): JSX.Element => (
    <Table<ErrorDetailDTO>
      rowKey={r => r.id}
      dataSource={record.details ?? []}
      pagination={false}
      size='small'
      columns={[
        {
          title: 'When',
          dataIndex: 'occurred_at',
          render: (v: string) => fmtDateTime(v),
          width: 200
        },
        { title: 'Stage', dataIndex: 'stage', width: 180 },
        { title: 'Op', dataIndex: 'operation', width: 160 },
        { title: 'Message', dataIndex: 'message', ellipsis: true },
        {
          title: 'Resolved',
          dataIndex: 'resolved_at',
          render: v => (v ? <Tag color='green'>yes</Tag> : <Tag color='orange'>open</Tag>),
          width: 120
        }
      ]}
    />
  )

  return (
    <Card
      title={
        <Space style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Space>
            <Typography.Title level={5} style={{ margin: 0 }}>
              Errors (last 24h)
            </Typography.Title>
            <Tooltip title='Aggregated from payments.nomi835_errors'>
              <InfoCircleOutlined />
            </Tooltip>
          </Space>
          <Button icon={<ReloadOutlined />} onClick={onReload}>
            Reload
          </Button>
        </Space>
      }
      className='rounded-2xl'
    >
      <Table<ErrorSummaryItemDTO>
        rowKey={r => r.file_name || Math.random().toString(36).slice(2)}
        dataSource={data}
        columns={columns}
        loading={loading}
        expandable={{ expandedRowRender }}
        pagination={{ pageSize: 10 }}
        size='middle'
      />
    </Card>
  )
}

export const Nomi835Dashboard: React.FC = () => {
  const { data, isLoading, isError, error, refetch, isFetching } = useNomi835Status()

  const files = data?.files ?? []
  const errors = data?.errors ?? []

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Nomi 835 files status</Title>
      <Space direction='vertical' size={16} style={{ width: '100%' }}>
        {isError && (
          <Alert
            type='error'
            showIcon
            message='Failed to load'
            description={(error as Error)?.message ?? String(error)}
          />
        )}

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card className='rounded-2xl'>
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title='Open errors (24h)'
                    value={errors.reduce((a, e) => a + e.open, 0)}
                    loading={isLoading || isFetching}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title='Files with errors'
                    value={errors.length}
                    loading={isLoading || isFetching}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card className='rounded-2xl'>
              <Typography.Paragraph style={{ marginBottom: 0 }}>
                Healthcheck window: {fmtDateTime(data?.range.from)} → {fmtDateTime(data?.range.to)}
              </Typography.Paragraph>
            </Card>
          </Col>
        </Row>

        <LastFilesTable data={files} loading={isLoading || isFetching} onReload={refetch} />

        <ErrorSummaryTable data={errors} loading={isLoading || isFetching} onReload={refetch} />
      </Space>
    </div>
  )
}
